// @ts-check
import React, { useEffect, useState, useRef } from "react"
import useToast from "../../../hooks/useToast.hook"
import useReportFiltersContext from "../../../hooks/contextConsumers/useReportFiltersContext.hook"
import ContextMenu from "../../../components/atoms/ContextMenu.atom"
import ModalFormEditTask from "../../../components/compounds/ModalFormEditTask.compound"
import ClientTasksTableContainer from "./tables/ClientTasksReport/ClientTasksTableContainer.controller"
import ResetButton from "./components/ResetButton"
import { useLocation, useNavigate } from "react-router-dom"
import UnifiedDatePicker from "../../../components/compounds/UnifiedDatePicker.compound"
import { thisWeekRange } from "../../../utils/date.utils"
import { UNIFIEDDATE_VALUES } from "../../../consts/CompoundRangeDatePicker"
import {
  getContextMenuItemsForIndividualReport,
  resolveDateRangeFromShortcutState,
} from "../../../utils/report.utils"
import DropdownClientPicker from "../../../components/compounds/DropdownClientPicker.compound"
import useCtrlClickTableTip from "../../../hooks/aikaTips/useCtrlClickTableTip.hook"

const INITIAL_SELECTED_CLIENT = null

/**
 * @typedef {import("../../../services/reports.service.types").GlobalTaskReportItem} GlobalTaskReportItem
 * @typedef {import("./HoursByClientReport.controller.types").HoursByClientReportControllerProps} HoursByClientReportControllerProps
 * @typedef {import("primereact/contextmenu").ContextMenu} PrimeContextMenu
 **/

const HoursByClientReport = React.forwardRef(
  /**
   * @param {HoursByClientReportControllerProps} props
   * @param {React.Ref<import("./tables/ClientTasksReport/ClientTasksTableContainer.controller.types").ClientTasksTableContainerControllerRef>} ref
   */
  (props, ref) => {
    const { downloadCsvButton } = props

    const tableRef = ref && typeof ref !== "function" ? ref : null

    const [contextMenuTarget, setContextMenuTarget] = useState(
      /** @type {GlobalTaskReportItem | null} */ (null)
    )
    const [loading, setLoading] = useState(false)
    const [editTaskVisible, setEditTaskVisible] = useState(false)
    const [updatingTask, setUpdatingTask] = useState(
      /** @type {GlobalTaskReportItem | null} */ (null)
    )

    const {
      tasksByClientClients,
      tasksByClientSelectedClient,
      setTasksByClientSelectedClient,
      tasksByClientPeriod,
      setTasksByClientPeriod,
      tasksByClientDateRange,
      setTasksByClientDateRange,
    } = useReportFiltersContext()

    const { hideFAB, showFAB } = useCtrlClickTableTip()

    useEffect(() => {
      showFAB()
      return () => hideFAB()
    }, [])

    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
      if (!location.state) {
        return
      }

      setTasksByClientSelectedClient(location.state.clientCode)
      setTasksByClientPeriod(location.state.period)

      const dateRange = resolveDateRangeFromShortcutState(location.state)

      if (dateRange) {
        setTasksByClientDateRange(dateRange)
      }

      navigate(location.pathname, { replace: true, state: null })
    }, [location.state])

    /** @type {React.MutableRefObject<PrimeContextMenu | null>} */
    const contextMenuRef = useRef(null)

    const { setErrorMessage } = useToast()

    const cmItems = [
      {
        label: "Editar",
        icon: "pi pi-pencil",
        command: async () => {
          setEditTaskVisible(true)
        },
      },
      {
        label: "Historial de cambios",
        icon: "pi pi-history",
        command: () => {
          navigate("/historial-tarea", {
            state: { taskId: contextMenuTarget?.taskId },
          })
        },
      },
    ]
    const controls = (
      <div className="flex justify-content-between align-items-center pb-3 ">
        <div className="mr-0 ml-1 flex flex-1 align-items-center gap-3">
          {tasksByClientClients.length > 1 ?
            <DropdownClientPicker
              clients={tasksByClientClients}
              selectedClient={tasksByClientSelectedClient}
              setSelectedClient={setTasksByClientSelectedClient}
            />
          : null}
          <UnifiedDatePicker
            dateRange={tasksByClientDateRange}
            setDateRange={setTasksByClientDateRange}
            period={tasksByClientPeriod}
            setPeriod={setTasksByClientPeriod}
            disabledChevrons={loading}
          />
        </div>
        {downloadCsvButton(tasksByClientSelectedClient === null)}
        <ResetButton
          onClick={() => {
            if (tasksByClientClients?.length > 1) {
              setTasksByClientSelectedClient(INITIAL_SELECTED_CLIENT)
            }
            tableRef?.current?.reset()
            setTasksByClientPeriod(UNIFIEDDATE_VALUES.SINGLEWEEK)
            setTasksByClientDateRange(thisWeekRange())
          }}
        />
      </div>
    )

    return (
      <>
        {controls}
        <ClientTasksTableContainer
          ref={tableRef}
          contextMenuRef={contextMenuRef}
          setContextMenuTarget={setContextMenuTarget}
          updatingTask={updatingTask}
          loading={loading}
          setLoading={setLoading}
        />
        <ContextMenu
          ref={contextMenuRef}
          // @ts-ignore
          model={getContextMenuItemsForIndividualReport(
            contextMenuTarget,
            cmItems
          )}
        />
        <ModalFormEditTask
          visible={editTaskVisible}
          setVisible={setEditTaskVisible}
          task={contextMenuTarget ?? undefined}
          teamMemberId={contextMenuTarget?.teamMemberId ?? null}
          onSubmit={() => {
            setUpdatingTask(contextMenuTarget)
          }}
          onEditCompleted={(errorMessage) => {
            if (errorMessage) {
              setErrorMessage({ message: errorMessage })
            }
            setEditTaskVisible(false)
            setUpdatingTask(null)
            tableRef?.current?.refresh()
          }}
          onlyActiveProjects={false}
          isAdminEdit
          waitForResponse={false}
        />
      </>
    )
  }
)

export default HoursByClientReport
